/**
 * The map inside Quick Scan: where the grid will actually sit.
 *
 * Local Falcon takes an explicit centre lat/lng plus a radius, and places a
 * grid_size x grid_size square of search points around it, where the radius is
 * the distance from the centre to the outermost N/E/S/W point (verified against
 * docs.localfalcon.com/openapi.yaml, 2026-09-02 — the radius reaches the EDGE,
 * not the corner). So the spacing between neighbouring points is
 * `2 * radius / (gridSize - 1)`, and drawing that here is the difference
 * between an operator guessing at coverage and seeing it.
 *
 * The pin is draggable and the map is clickable: both move the centre, which is
 * the whole point of the control — a scan centred on the shop tells you nothing
 * about the suburb you are actually trying to win.
 *
 * Gated by VITE_GOOGLE_MAPS_API_KEY exactly like
 * components/keyword-research/LocationMap.tsx: with no key this renders an
 * inline notice and the dialog still works, falling back to scanning from the
 * business's own coordinates.
 */
import { useEffect, useMemo } from "react";
import {
	APIProvider,
	Map as GoogleMap,
	AdvancedMarker,
	Pin,
	useMap,
	type MapMouseEvent,
} from "@vis.gl/react-google-maps";

const API_KEY = (import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string) || "";

/**
 * AdvancedMarker needs a Map ID that actually exists in the Cloud console —
 * with an unregistered one the pins do not render (developers.google.com/maps/
 * documentation/javascript/advanced-markers/start). Google publishes
 * DEMO_MAP_ID for exactly this case, so an unconfigured environment gets a
 * working map instead of an invisible pin; production should set a real ID via
 * VITE_GOOGLE_MAPS_MAP_ID.
 */
export const mapIdFromEnv = (configured: string | undefined): string =>
	(configured || "").trim() || "DEMO_MAP_ID";

const MAP_ID = mapIdFromEnv(
	import.meta.env.VITE_GOOGLE_MAPS_MAP_ID as string | undefined,
);

// Google Maps overlay APIs parse these strings themselves rather than handing
// them to CSS, so `var(--color-coral)` would not resolve. Keep aligned with the
// theme tokens in frontend/src/index.css.
const MAP_COLORS = {
	grid: "#0A76A8",
	gridFaint: "#6B7280",
} as const;

const KM_PER_DEGREE_LAT = 111.32;
const KM_PER_MILE = 1.609344;

/**
 * The slice of the Google Maps global we touch, typed structurally.
 *
 * @types/google.maps is installed but its `google` namespace is not in this
 * project's ambient scope (LocationMap.tsx hits the same wall), so describing
 * the two overlay constructors here keeps this file clean under tsc rather
 * than adding two more errors to the pile.
 */
interface MapsOverlay {
	setMap: (map: unknown) => void;
}

interface MapsNamespace {
	Polyline: new (options: Record<string, unknown>) => MapsOverlay;
	Rectangle: new (options: Record<string, unknown>) => MapsOverlay;
	LatLngBounds: new (sw: LatLng, ne: LatLng) => unknown;
}

export interface LatLng {
	lat: number;
	lng: number;
}

export interface ScanGridMapProps {
	/** Where the grid is centred right now. */
	center: LatLng;
	/** The mapped listing's own coordinates, drawn so the offset is visible. */
	business: LatLng | null;
	gridSize: number;
	radius: number;
	measurement: "mi" | "km";
	onCenterChange: (next: LatLng) => void;
	disabled?: boolean;
}

/** Grid half-width in kilometres — centre to the outermost cardinal point. */
const radiusKm = (radius: number, measurement: "mi" | "km"): number =>
	measurement === "km" ? radius : radius * KM_PER_MILE;

/**
 * The scan points Local Falcon will use, as latitudes and longitudes.
 *
 * Longitude degrees shrink with latitude, so the correction by cos(lat) is what
 * keeps the drawn grid square on the ground rather than only on the screen.
 */
export function gridOffsets(
	center: LatLng,
	gridSize: number,
	radius: number,
	measurement: "mi" | "km",
): { lats: number[]; lngs: number[] } {
	const km = radiusKm(radius, measurement);
	const dLat = km / KM_PER_DEGREE_LAT;
	const cos = Math.cos((center.lat * Math.PI) / 180);
	// At the poles cos(lat) approaches 0 and the longitude span explodes; clamp
	// so the grid stays drawable instead of spanning the globe.
	const dLng = km / (KM_PER_DEGREE_LAT * Math.max(cos, 0.01));
	const steps = Math.max(gridSize - 1, 1);
	const lats: number[] = [];
	const lngs: number[] = [];
	for (let i = 0; i < gridSize; i += 1) {
		const t = gridSize === 1 ? 0 : -1 + (2 * i) / steps;
		lats.push(center.lat + t * dLat);
		lngs.push(center.lng + t * dLng);
	}
	return { lats, lngs };
}

/**
 * Draws the grid lines imperatively.
 *
 * Rows and columns rather than a marker per point on purpose: a 21x21 grid is
 * 441 points, and 441 map markers is a visibly janky dialog, while 42
 * polylines is not — the intersections read as the scan points either way.
 */
function GridOverlay({
	center,
	gridSize,
	radius,
	measurement,
}: Omit<ScanGridMapProps, "onCenterChange" | "business" | "disabled">) {
	const map = useMap();
	const { lats, lngs } = useMemo(
		() => gridOffsets(center, gridSize, radius, measurement),
		[center, gridSize, radius, measurement],
	);

	useEffect(() => {
		const maps = (window as unknown as { google?: { maps?: MapsNamespace } })
			.google?.maps;
		if (!map || !maps) return;

		const south = lats[0];
		const north = lats[lats.length - 1];
		const west = lngs[0];
		const east = lngs[lngs.length - 1];

		const style = {
			strokeColor: MAP_COLORS.grid,
			strokeOpacity: 0.45,
			strokeWeight: 1,
			clickable: false,
			map,
		};
		const lines: MapsOverlay[] = [];
		lats.forEach((lat) => {
			lines.push(
				new maps.Polyline({
					...style,
					path: [
						{ lat, lng: west },
						{ lat, lng: east },
					],
				}),
			);
		});
		lngs.forEach((lng) => {
			lines.push(
				new maps.Polyline({
					...style,
					path: [
						{ lat: south, lng },
						{ lat: north, lng },
					],
				}),
			);
		});
		const extent = new maps.Rectangle({
			bounds: { north, south, east, west },
			strokeColor: MAP_COLORS.gridFaint,
			strokeOpacity: 0.8,
			strokeWeight: 1,
			fillColor: MAP_COLORS.grid,
			fillOpacity: 0.05,
			clickable: false,
			map,
		});

		map.fitBounds(
			new maps.LatLngBounds(
				{ lat: south, lng: west },
				{ lat: north, lng: east },
			),
			24,
		);

		return () => {
			lines.forEach((line) => line.setMap(null));
			extent.setMap(null);
		};
	}, [map, lats, lngs]);

	return null;
}

function ScanGridMapInner({
	center,
	business,
	gridSize,
	radius,
	measurement,
	onCenterChange,
	disabled = false,
}: ScanGridMapProps) {
	const move = (event: MapMouseEvent) => {
		const latLng = event.detail.latLng;
		if (!latLng || disabled) return;
		onCenterChange({ lat: latLng.lat, lng: latLng.lng });
	};

	return (
		<GoogleMap
			mapId={MAP_ID}
			defaultCenter={center}
			defaultZoom={12}
			gestureHandling="greedy"
			disableDefaultUI
			zoomControl
			onClick={move}
			className="h-56 w-full overflow-hidden rounded-md border border-input"
		>
			<GridOverlay
				center={center}
				gridSize={gridSize}
				radius={radius}
				measurement={measurement}
			/>
			{business && (
				<AdvancedMarker position={business} title="The business">
					<Pin
						background={MAP_COLORS.gridFaint}
						borderColor={MAP_COLORS.gridFaint}
						glyphColor="#FFFFFF"
						scale={0.7}
					/>
				</AdvancedMarker>
			)}
			<AdvancedMarker
				position={center}
				draggable={!disabled}
				title="Grid centre — drag to move"
				onDragEnd={(event) => {
					const lat = event.latLng?.lat();
					const lng = event.latLng?.lng();
					if (lat === undefined || lng === undefined) return;
					onCenterChange({ lat, lng });
				}}
			>
				<Pin
					background={MAP_COLORS.grid}
					borderColor={MAP_COLORS.grid}
					glyphColor="#FFFFFF"
				/>
			</AdvancedMarker>
		</GoogleMap>
	);
}

export default function ScanGridMap(props: ScanGridMapProps) {
	if (!API_KEY) {
		return (
			<div
				className="rounded border border-dashed border-border bg-warm-bg p-3 text-xs text-muted-foreground"
				data-testid="quick-scan-map-disabled"
			>
				Map preview disabled —{" "}
				<code className="rounded bg-card px-1 py-0.5">
					VITE_GOOGLE_MAPS_API_KEY
				</code>{" "}
				is not set. The scan will be centred on the business&apos;s own
				coordinates.
			</div>
		);
	}

	return (
		<div data-testid="quick-scan-map">
			<APIProvider apiKey={API_KEY}>
				<ScanGridMapInner {...props} />
			</APIProvider>
		</div>
	);
}
